import { GenericTileProps } from '@christies/react/dist/lib/components/Tiles/GenericTile/GenericTile.types'
import { ComponentStatus } from '@christies/react/typings'
import { useMockData } from '../../useMockData'
import useSWR from 'swr'
import type { AuctionResponse, Exhibition, ParsedData, ParsedDataResponse, RelatedSalesKey } from './types'
import { dataFetcher } from './dataFetcher'
import { useRelatedContentData } from '../../useRelatedContentData'
import { Endpoint } from '../../dataFetcher'
import { Utils } from '@christies/react/.'
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react'
import { sanitizeUrl } from '../../utils'

const urlDecoder = ({
  url,
  title,
  id,
  saleRoomCode,
}: {
  url: string
  title?: string
  id?: string
  saleRoomCode?: string
}): string => {
  const decoder: Record<string, string | undefined> = {
    '{title}': title,
    '{id}': id,
    '{saleRoomCode}': saleRoomCode,
  }

  return url?.replace(/{title}|{id}|{saleRoomCode}/gi, (match) => decoder[match] as string).toLowerCase()
}

export const useRelatedSalesData = (
  key: RelatedSalesKey,
  mockPromise: Promise<{ default: GenericTileProps[] }>
): { status: ComponentStatus; data?: GenericTileProps[] } => {
  const { sitecoreContext: context } = useSitecoreContext()
  const { storyAPIEndpoint, auctionsAPIEndpoint, exhibitionsAPIEndpoint, auctionUrlFormat, exhibitionUrlFormat } = key
  const { data: mockData, status: mockStatus } = useMockData<GenericTileProps[]>(mockPromise)
  const { data: relatedContentData, status: relatedContentStatus } = useRelatedContentData(
    mockStatus === 'error' ? storyAPIEndpoint : null
  )

  const relatedSales = relatedContentData?.relatedContent?.find(
    (content) => content?.type?.toLowerCase() === 'auctions-exhibitions'
  )?.values

  const endpoints: Endpoint[] = []

  if (relatedSales?.length) {
    const auctionIds = relatedSales?.filter((item) => item.type.toLowerCase() === 'auctions').map((item) => item.id)

    if (auctionIds.length) {
      endpoints.push({
        ...auctionsAPIEndpoint,
        parameters: {
          ...auctionsAPIEndpoint.parameters,
          ids: auctionIds.join(','),
        },
      })
    }

    const exhibitionIds = relatedSales
      ?.filter((item) => item.type.toLowerCase() === 'exhibitions')
      .map((item) => item.id)

    if (exhibitionIds.length) {
      exhibitionIds.forEach((id) =>
        endpoints.push({
          ...exhibitionsAPIEndpoint,
          url: `${exhibitionsAPIEndpoint.url.replace('{id}', id)}`,
          parameters: {
            ...exhibitionsAPIEndpoint.parameters,
          },
        })
      )
    }
  }

  const { data, isLoading, error } = useSWR(relatedContentStatus === 'ready' ? endpoints : null, dataFetcher)

  if (mockStatus !== 'error') {
    return { data: mockData, status: mockStatus }
  }

  const relatedSalesLocations = context?.['Discovery.Feature.Magazines.StoriesLocations'] as Record<string, string>
  const relatedSalesAuctionTypes = context?.['Discovery.Feature.Magazines.StoriesAuctionTypes'] as Record<
    string,
    string
  >
  const relatedSalesExhibitionTypes = (
    context?.['Discovery.Feature.Magazines.StoriesExhibitionTypes'] as {
      PrivateSaleExhibition: string
    }
  )?.PrivateSaleExhibition

  const getTranslation = (translations: Record<string, string>, targetKey: string, fallbackValue?: string): string =>
    (translations && translations[targetKey]) || fallbackValue || targetKey

  const parsedData: ParsedDataResponse | undefined = data
    ?.filter((data): data is AuctionResponse | Exhibition => !!data)
    .flatMap((response) => {
      if (Utils.isType<AuctionResponse>(response, 'auctions')) {
        return response.auctions.map((auction) => {
          const auctionLink = urlDecoder({
            url: auctionUrlFormat,
            title: sanitizeUrl(auction.title),
            id: auction.id,
            saleRoomCode: auction.auctionRoomCode,
          })

          return {
            id: auction.id,
            image: {
              src: auction.images.primary.href,
              alt: auction.images.alt,
            },
            primaryTitle: auction.title,
            date: Utils.formatDate(auction.startDate, auction.endDate, context.language),
            type: getTranslation(relatedSalesAuctionTypes, auction.type),
            location: getTranslation(relatedSalesLocations, auction.auctionRoomCode, auction.location),
            cta: {
              href: auctionLink,
            },
          }
        })
      } else {
        const exhibitionLink = urlDecoder({
          url: exhibitionUrlFormat,
          title: sanitizeUrl(response.title),
          id: response.id,
        })

        return {
          id: response.id,
          image: {
            src: response.media[0].primary.href,
            alt: response.media[0].alt,
          },
          primaryTitle: response?.title,
          date: Utils.formatDate(response?.startDate, response.endDate, context.language),
          type: relatedSalesExhibitionTypes || response?.type,
          location: getTranslation(relatedSalesLocations, response?.locations[0]),
          cta: {
            href: exhibitionLink,
          },
        }
      }
    })

  const sortedData = relatedSales
    ?.map(({ id: sortId }) => parsedData?.find(({ id }) => sortId === id))
    .filter((auction): auction is ParsedData => !!auction)

  const getComponentStatus = (): ComponentStatus => {
    if (isLoading || relatedContentStatus === 'initial') {
      return 'initial'
    }
    if (error || !sortedData?.length) {
      return 'error'
    }
    return 'ready'
  }

  return { data: sortedData, status: getComponentStatus() }
}
